import { type ReactNode } from 'react'

import { Descriptions, IconDelete, Space, SubText } from '@procurify/ui'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { PaginationControls } from '@webapp/core/components/PaginationControls'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExternalBankAccountTypes } from '@webapp/platform/banking/entities/enums/ExternalBankAccountTypeEnum'
import { type ExternalBankAccount } from '@webapp/platform/banking/entities/ExternalBankAccount'
import messages from './messages'

export interface IExternalBankAccountConfigTableProps {
  accounts: ExternalBankAccount[]
  isLoading: boolean
  isError: boolean
  onDeleteAccount: (account: ExternalBankAccount) => void
  actionSlot?: ReactNode
}

export const ExternalBankAccountConfigTable = ({
  accounts,
  isLoading,
  isError,
  onDeleteAccount,
  actionSlot,
}: IExternalBankAccountConfigTableProps) => {
  const intl = useProcurifyIntl()
  const tableData = isError ? [] : accounts

  const typeLabels = {
    [ExternalBankAccountTypes.OTHER]: intl.formatMessage(messages.typeOther),
    [ExternalBankAccountTypes.CHECK]: intl.formatMessage(messages.typeCheck),
    [ExternalBankAccountTypes.ACH]: intl.formatMessage(messages.typeAch),
    [ExternalBankAccountTypes.EFT]: intl.formatMessage(messages.typeEft),
    [ExternalBankAccountTypes.WIRE]: intl.formatMessage(messages.typeWire),
  }

  // Type-conditional fields render as stacked label/value lines (BHV-004);
  // ACH routing/account numbers arrive server-masked and render as-is
  // (BHV-005).
  const getDetailLines = (account: ExternalBankAccount): ReactNode => {
    if (account.isCheck()) {
      return (
        <Descriptions size='small' valueWidth='fill'>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailStartNumber)}
          >
            {account.data.startNumber}
          </Descriptions.Item>
        </Descriptions>
      )
    }

    if (account.isACH()) {
      return (
        <Descriptions size='small' valueWidth='fill'>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailBankName)}
          >
            {account.data.bankName}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailBankRoutingNumber)}
          >
            {account.data.bankRoutingNumber}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailCompanyName)}
          >
            {account.data.companyName}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailCompanyRoutingNumber)}
          >
            {account.data.companyRoutingNumber}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailCompanyTaxId)}
          >
            {account.data.companyTaxId}
          </Descriptions.Item>
        </Descriptions>
      )
    }

    if (account.hasDescriptionData()) {
      return (
        <Descriptions size='small' valueWidth='fill'>
          <Descriptions.Item
            label={intl.formatMessage(messages.detailDescription)}
          >
            {account.data.description}
          </Descriptions.Item>
        </Descriptions>
      )
    }

    return null
  }

  // Under the table's `table-layout: auto` a `width` hint (border-box, 24px of
  // it padding) is the width a column keeps, not a cap (DataTable does not
  // forward `maxWidth`), so each hint is only what its content needs on one
  // line, plus a few px: the header for Payment method and Currency, a code of
  // about 14 characters for GL code, and a name of about 27 characters for
  // Account name; longer values wrap. Details, the row's main content, has no
  // hint and takes the remaining width — about 277px at 1200px with the sidebar
  // expanded, which the `Descriptions` grid splits into a label column sized to
  // that row's longest label and a value column taking the rest, so a long
  // value wraps in its own column instead of widening the table.
  const columns = [
    col('BASIC', {
      label: intl.formatMessage(messages.columnAccountName),
      width: 220,
      getter: (account: ExternalBankAccount) => (
        <SubText bold autoMargin={false}>
          {account.name}
        </SubText>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnPaymentMethod),
      width: 124,
      getter: (account: ExternalBankAccount) => typeLabels[account.type],
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnDetails),
      getter: getDetailLines,
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnGlCode),
      width: 130,
      getter: (account: ExternalBankAccount) => account.glCode || '--',
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnCurrency),
      width: 80,
      getter: (account: ExternalBankAccount) => account.currency.name,
    }),
  ]

  // Sort keys mirror Angular: name / currency / type (BHV-007).
  const sortKeys = [
    { label: intl.formatMessage(messages.sortAccountName), value: 'name' },
    { label: intl.formatMessage(messages.sortCurrency), value: 'currency' },
    { label: intl.formatMessage(messages.sortPaymentMethod), value: 'type' },
  ]

  const getMenuListItems = (account: ExternalBankAccount) => [
    {
      icon: <IconDelete />,
      text: intl.formatMessage(messages.deleteAccountAction),
      onClick: () => onDeleteAccount(account),
    },
  ]

  return (
    <PaginationControls id='bank-account-configs'>
      <Space size='large'>
        <Space direction='horizontal' justify='space-between' align='center'>
          <Space direction='horizontal'>
            <PaginationControls.Search />
            <Space.Item minWidth={200}>
              <PaginationControls.Sort sortKeys={sortKeys} />
            </Space.Item>
          </Space>
          {actionSlot}
        </Space>

        <DataTable<ExternalBankAccount>
          id='external-bank-account-config-table'
          aria-label={intl.formatMessage(messages.tableAriaLabel)}
          data={tableData}
          isLoading={isLoading}
          // One skeleton row per listed row keeps the row count during a
          // refetch; with no rows yet, DataTable's default count applies.
          loaderRows={tableData.length || undefined}
          columns={columns}
          customizableColumns={false}
          persistCustomizedColumns={false}
          emptyStateText={
            isError
              ? intl.formatMessage(messages.errorStateText)
              : intl.formatMessage(messages.emptyStateTitle)
          }
          emptyStateSize='small'
          menuListItems={getMenuListItems}
          menuListAriaLabel={(account) =>
            intl.formatMessage(messages.accountActionsAriaLabel, {
              name: account.name,
            })
          }
          zebra={false}
        />

        <Space direction='horizontal' justify='space-between' align='center'>
          <PaginationControls.PageLimit />
          <PaginationControls.Page />
        </Space>
      </Space>
    </PaginationControls>
  )
}

ExternalBankAccountConfigTable.displayName = 'ExternalBankAccountConfigTable'
